import React, { useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Grid, Box, Icon, Typography, TableBody, TableHead, TableRow, Button } from '@mui/material';
import { StyledTable } from 'components/common/Styles';
import { TableCellWithWidth } from 'components/list/TableComponentsStyled';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { useToast } from 'hooks/toasts/useToast';
import { get, useChangeTrackingLens, set, sequence } from '@totalsoft/change-tracking-react';
import { remove } from 'ramda';
import AccountStatementIndicatorsListItem from './AccountStatementIndicatorsListItem';
import { REMOVE_ACCOUNT_STATEMENT_INDICATOR } from '../../mutations';
import { ACCOUNT_STATEMENT_INDICATOR_UPDATED, ACCOUNT_STATEMENT_INDICATOR_DELETED } from '../../subscriptions';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { RAW_FINANCIAL_INDICATORS_QUERY } from '../../../../financialIndicators/components/list/queries';
import AccountStatementIndicatorsModalComponent from './AccountStatementIndicatorsModalComponent';
import { accountStatementIndicatorModalVar } from './variables';

const AccountStatementIndicatorsContainer = ({ canWrite, statementIndicators, configId, usedByPartner }) => {
  const isNewStatement = useMemo(() => !configId, [configId]);
  const { t } = useTranslation();
  const [lens, , resetLens] = useChangeTrackingLens([]);
  const addToast = useToast();

  const { data: financialIndicatorData } = useQuery(RAW_FINANCIAL_INDICATORS_QUERY);
  const financialIndicatorList = useMemo(() => financialIndicatorData?.financialIndicators || [], [financialIndicatorData]);

  // #region effect
  useEffect(() => {
    resetLens(
      statementIndicators?.map(element => {
        const elementLocal = { ...element };
        elementLocal.isInEditing = false;
        elementLocal.isSaving = false;
        elementLocal.financialIndicatorName = financialIndicatorList?.find(x => x.id === element.financialIndicatorId)?.name;
        return elementLocal;
      })
    );
  }, [statementIndicators, financialIndicatorList, resetLens]);

  // #endregion effect

  // #region alter
  useSubscription(ACCOUNT_STATEMENT_INDICATOR_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('AccountStatement.IndicatorSaved'), 'success');
      client.cache.modify({
        id: `AccountStatementItem:{"configId":${configId}}`,
        fields: {
          accountStatementIndicators(existing, { toReference }) {
            if (existing.some(x => x.__ref === toReference(data.accountStatementIndicatorUpdated.accountStatementIndicator)?.__ref))
              return [...existing];
            return [...existing, toReference(data.accountStatementIndicatorUpdated.accountStatementIndicator)];
          }
        }
      });
      accountStatementIndicatorModalVar({ open: false });
    }
  });

  const [deleteAccountStatementIndicator] = useMutation(REMOVE_ACCOUNT_STATEMENT_INDICATOR);

  useSubscription(ACCOUNT_STATEMENT_INDICATOR_DELETED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('AccountStatement.IndicatorDeleted'), 'success');
      const lensLocal = [...(lens |> get)];
      const objTL = lensLocal.filter(el => el.statementIndicatorId !== data.accountStatementIndicatorDeleted.statementIndicatorId);
      resetLens(objTL);
      client.cache.evict({
        id: `AccountStatementIndicatorItem:{"statementIndicatorId":${data.accountStatementIndicatorDeleted.statementIndicatorId}}`
      });
      client.cache.gc();
    }
  });
  //endregion alter

  // #region handlers
  const handleAdd = useCallback(() => {
    var maxOrderNo = Math.max(...(lens |> get).map(x => x.orderNo), 0);
    var maxComputeOrderNo = Math.max(...(lens |> get).map(x => x.computeOrder), 0);
    accountStatementIndicatorModalVar({
      open: true,
      item: {
        orderNo: maxOrderNo ? maxOrderNo + 1 : 1,
        computeOrder: maxComputeOrderNo ? maxComputeOrderNo + 1 : 1
      }
    });
  }, [lens]);

  const handleDelete = useCallback(
    (statementIndicatorId, index) => {
      if (window.confirm(t('AccountStatement.DeleteIndicatorConfirmation'))) {
        if (!statementIndicatorId) set(lens)(remove(index, 1, lens |> get));
        deleteAccountStatementIndicator({ variables: { input: { statementIndicatorId } } });
      }
    },
    [deleteAccountStatementIndicator, lens, t]
  );
  //  #endregion handlers

  return (
    <>
      <AccountStatementIndicatorsModalComponent configId={configId} indicators={lens |> get} />
      {isNewStatement ? (
        <Grid container flexDirection='column' gap={1} p={2}>
          <Box display='flex' gap={1} alignItems='center' justifyContent='center'>
            <Icon fontSize='small' color='primary'>
              error
            </Icon>
            <Typography color='textPrimary'>{t('AccountStatement.IndicatorsWarning')}</Typography>
          </Box>
        </Grid>
      ) : (
        <>
          <StyledTable>
            <TableHead>
              <TableRow>
                <TableCellWithWidth style={{ fontWeight: 'bold', align: 'center' }} w={5}>
                  {t('AccountStatement.Indicators.OrderNo')}
                </TableCellWithWidth>
                <TableCellWithWidth style={{ fontWeight: 'bold', align: 'center' }} w={10}>
                  {t('AccountStatement.Indicators.Code')}
                </TableCellWithWidth>
                <TableCellWithWidth style={{ fontWeight: 'bold', align: 'center' }} w={30}>
                  {t('AccountStatement.Indicators.Name')}
                </TableCellWithWidth>
                <TableCellWithWidth style={{ fontWeight: 'bold', align: 'center' }} w={10}>
                  {t('AccountStatement.Indicators.AttributeName')}
                </TableCellWithWidth>
                <TableCellWithWidth style={{ fontWeight: 'bold', align: 'center' }} w={10}>
                  {t('AccountStatement.Indicators.ComputeOrder')}
                </TableCellWithWidth>
                <TableCellWithWidth style={{ fontWeight: 'bold', align: 'center' }} w={10}>
                  {t('AccountStatement.Indicators.ReadOnly') + '/' + t('AccountStatement.Indicators.Visible')}
                </TableCellWithWidth>
                <TableCellWithWidth align='right' w={10}>
                  {usedByPartner ? (
                    <></>
                  ) : (
                    <Button
                      color='primary'
                      variant='contained'
                      startIcon={<AddIcon />}
                      onClick={handleAdd}
                      disabled={isNewStatement || !canWrite}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {t('Add')}
                    </Button>
                  )}
                </TableCellWithWidth>
              </TableRow>
            </TableHead>
            <TableBody>
              {sequence(lens)?.map((statementIndicatorLens, index) => {
                return (
                  <AccountStatementIndicatorsListItem
                    key={`${index}_${statementIndicatorLens.statementIndicatorId |> get}`}
                    canWrite={canWrite}
                    statementIndicatorLens={statementIndicatorLens}
                    index={index}
                    onDelete={handleDelete}
                    usedByPartner={usedByPartner}
                  />
                );
              })}
            </TableBody>
          </StyledTable>
        </>
      )}
    </>
  );
};

AccountStatementIndicatorsContainer.propTypes = {
  canWrite: PropTypes.bool,
  statementIndicators: PropTypes.array,
  configId: PropTypes.number,
  usedByPartner: PropTypes.bool
};

export default AccountStatementIndicatorsContainer;
